<template>
  <div class="space-y-6">
    <header class="flex items-center justify-between">
      <div>
        <h1 class="text-2xl font-semibold text-white">Equipment Drafts</h1>
        <p class="text-sm text-slate-400 mt-1">
          Resume saved onboarding drafts or promote them to active equipment types.
        </p>
      </div>
      <button class="btn-secondary" @click="refreshDrafts">Refresh</button>
    </header>

    <section class="panel-neo panel-neo--glassier p-6 rounded-2xl space-y-4">
      <div class="flex flex-wrap items-center gap-4">
        <div class="flex items-center gap-2">
          <label class="text-xs uppercase tracking-[0.2em] text-slate-400">Search</label>
          <input
            v-model="search"
            type="text"
            placeholder="Filter by make, model, or name"
            class="input-dark w-80"
          />
        </div>
        <div class="flex items-center gap-2 text-xs text-slate-400">
          <span>Progress:</span>
          <button
            v-for="step in steps"
            :key="step"
            class="admin-chip"
            :class="{ 'admin-chip--active': progress === step }"
            @click="progress = step"
          >
            {{ step === 'all' ? 'All' : stepLabel(step) }}
          </button>
        </div>
      </div>

      <div v-if="loading" class="py-24 text-center text-slate-400">Loading drafts…</div>
      <div v-else-if="error" class="py-24 text-center text-rose-400">{{ error }}</div>
      <div v-else-if="!filteredDrafts.length" class="py-24 text-center text-slate-400">
        No drafts match your filters.
      </div>
      <div v-else class="grid gap-3 grid-cols-[repeat(auto-fit,minmax(260px,1fr))]">
        <article
          v-for="draft in filteredDrafts"
          :key="draft.id"
          class="rounded-xl border border-slate-800/60 bg-slate-900/70 p-3 transition hover:border-primary-500/40"
        >
          <div class="flex flex-col gap-3">
            <div class="space-y-2">
              <h2 class="text-sm text-white font-semibold">
                {{ draft.name || autoName(draft) || 'Untitled Draft' }}
              </h2>
              <p class="text-xs text-slate-400">
                {{ draft.make || 'Unknown Make' }}
                <span v-if="draft.model">• {{ draft.model }}</span>
                <span v-if="draft.descriptor">— {{ draft.descriptor }}</span>
              </p>
              <div class="flex flex-wrap gap-2 text-[10px] text-slate-400">
                <span class="badge badge--outline">Step: {{ stepLabel(draft.progress_step ?? 'core') }}</span>
                <span class="badge badge--outline">
                  Updated {{ formatDate(draft.updated_at) }}
                </span>
              </div>
            </div>

            <div class="flex items-center justify-end gap-2 text-xs">
              <button class="btn-secondary btn-secondary--xs" @click="resumeDraft(draft)">Resume</button>
              <button class="btn-primary btn-primary--xs" @click="publishDraft(draft)">Publish</button>
              <button class="btn-danger btn-danger--xs" @click="confirmDelete(draft)">Delete</button>
            </div>
          </div>
        </article>
      </div>
    </section>

    <EquipmentTypeOnboarding
      v-if="onboardingOpen"
      :open="onboardingOpen"
      :inventory-types="inventory.types"
      :products="inventory.products"
      :draft-id="activeDraftId"
      :loading="onboardingSaving"
      :enable-drafts="true"
      @close="closeOnboarding"
      @submit="handleOnboardingSubmit"
    />
  </div>
</template>

<script setup lang="ts">
import { computed, onMounted, ref } from 'vue'
import { useInventoryStore } from '@/stores/inventory'
import type { EquipmentTypeDraft, EquipmentTypeCreateInput } from '@/types/inventory'
import {
  listEquipmentDrafts,
  deleteEquipmentDraft,
  createEquipmentTypeWithDetails,
} from '@/lib/inventoryApi'
import EquipmentTypeOnboarding from '@/components/modals/EquipmentTypeOnboarding.vue'
import { createLogger } from '@/lib/logger'

const log = createLogger('AdminEquipmentDrafts')
const inventory = useInventoryStore()
const drafts = ref<EquipmentTypeDraft[]>([])
const loading = ref(false)
const error = ref<string | null>(null)
const search = ref('')
const progress = ref<'all' | 'core' | 'specs' | 'assets' | 'accessories' | 'review'>('all')
const onboardingOpen = ref(false)
const onboardingSaving = ref(false)
const activeDraftId = ref<number | null>(null)

const steps: Array<'all' | 'core' | 'specs' | 'assets' | 'accessories' | 'review'> = [
  'all',
  'core',
  'specs',
  'assets',
  'accessories',
  'review',
]

const filteredDrafts = computed(() => {
  const term = search.value.trim().toLowerCase()
  return drafts.value.filter((draft) => {
    const matchesProgress = progress.value === 'all' || draft.progress_step === progress.value
    const matchesSearch =
      !term ||
      (draft.name ?? '').toLowerCase().includes(term) ||
      (draft.make ?? '').toLowerCase().includes(term) ||
      (draft.model ?? '').toLowerCase().includes(term) ||
      (draft.descriptor ?? '').toLowerCase().includes(term)
    return matchesProgress && matchesSearch
  })
})

function stepLabel(step: string) {
  return step.replace(/_/g, ' ').replace(/^\w/, (c) => c.toUpperCase())
}

function autoName(draft: EquipmentTypeDraft) {
  const make = draft.make ?? ''
  const model = draft.model ?? ''
  return [make, model].filter(Boolean).join(' ')
}

function formatDate(value: string) {
  return new Date(value).toLocaleString()
}

async function loadDrafts(force = false) {
  if (loading.value) return
  loading.value = true
  error.value = null
  try {
    if (force) {
      await inventory.loadDrafts(true)
    } else if (!inventory.drafts.length) {
      await inventory.loadDrafts()
    }
    drafts.value = await listEquipmentDrafts()
  } catch (err) {
    log.error('loadDrafts failed', err)
    error.value = err instanceof Error ? err.message : String(err)
  } finally {
    loading.value = false
  }
}

function refreshDrafts() {
  loadDrafts(true)
}

function resumeDraft(draft: EquipmentTypeDraft) {
  activeDraftId.value = draft.id
  onboardingOpen.value = true
}

function closeOnboarding() {
  onboardingOpen.value = false
  activeDraftId.value = null
}

async function publishDraft(draft: EquipmentTypeDraft) {
  if (onboardingSaving.value) return
  onboardingSaving.value = true
  try {
    const payload: EquipmentTypeCreateInput = {
      status: 'active',
      name: draft.name ?? autoName(draft),
      make: draft.make ?? '',
      model: draft.model ?? '',
      descriptor: draft.descriptor,
      categoryId: draft.category_id ?? undefined,
      isAccessory: draft.is_accessory,
      products: draft.products ?? undefined,
      specs: draft.specs ?? undefined,
      assets: draft.assets ?? undefined,
      accessories: draft.accessories ?? undefined,
    }
    await createEquipmentTypeWithDetails(payload)
    await deleteEquipmentDraft(draft.id)
    await loadDrafts(true)
  } catch (err) {
    log.error('publish failed', err)
  } finally {
    onboardingSaving.value = false
  }
}

async function confirmDelete(draft: EquipmentTypeDraft) {
  try {
    await deleteEquipmentDraft(draft.id)
    await loadDrafts(true)
  } catch (error) {
    log.error('delete failed', error)
  }
}

async function handleOnboardingSubmit(payload: EquipmentTypeCreateInput) {
  if (onboardingSaving.value) return
  onboardingSaving.value = true
  try {
    await createEquipmentTypeWithDetails(payload)
    if (activeDraftId.value) {
      await deleteEquipmentDraft(activeDraftId.value)
    }
    await loadDrafts(true)
    onboardingOpen.value = false
  } catch (err) {
    log.error('submit failed', err)
  } finally {
    onboardingSaving.value = false
  }
}

async function handleOnboardingDraft() {
  await loadDrafts(true)
  return { id: activeDraftId.value ?? undefined }
}

onMounted(() => {
  loadDrafts()
  if (!inventory.products.length && !inventory.productsLoading) {
    inventory.loadProducts()
  }
})
</script>

<style scoped>
.admin-chip {
  padding: 0.25rem 0.75rem;
  border-radius: 999px;
  border: 1px solid rgba(71, 85, 105, 0.7);
  background: rgba(15, 23, 42, 0.8);
  color: rgba(148, 163, 184, 0.85);
  transition: border 0.2s ease, color 0.2s ease;
}

.admin-chip--active {
  border-color: rgba(14, 165, 233, 0.6);
  color: rgba(125, 211, 252, 0.95);
  background: rgba(14, 165, 233, 0.12);
}

.badge {
  display: inline-flex;
  align-items: center;
  border: 1px solid rgba(71, 85, 105, 0.6);
  padding: 0.125rem 0.5rem;
  border-radius: 0.5rem;
  text-transform: uppercase;
  letter-spacing: 0.18em;
}

.badge--outline {
  border-color: rgba(51, 65, 85, 0.6);
  color: rgba(203, 213, 225, 0.85);
}
</style>

